import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Entities() {
  const { isAdmin } = useAuth();
  const [entities, setEntities] = useState([]);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const load = () => api.get("/entities").then((r) => setEntities(r.data));

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!query) return entities;
    const q = query.toLowerCase();
    return entities.filter((e) =>
      [e.name, e.title, e.entity_type, e.threat_level, e.body, e.id]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [entities, query]);

  const createEntity = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/entities", {
        name: newName.trim(),
        title: "",
        classification: "UNCLASSIFIED",
        status: "ACTIVE",
        date: "",
        body: "",
        images: [],
        entity_type: "",
        threat_level: "",
        sightings: "",
      });
      setNewName("");
      setCreating(false);
      toast.success("Entity inscribed.");
      load();
    } catch (e) {
      toast.error("Inscription failed.");
    }
  };

  return (
    <div className="space-y-12" data-testid="entities-page">
      <header className="space-y-6">
        <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#A3A3A3]">
          Section III · Catalogue Daemonologica
        </div>
        <h1 className="font-serif-display text-5xl sm:text-6xl tracking-tighter leading-none">
          Entities
        </h1>
        <p className="text-sm text-[#A3A3A3] max-w-2xl leading-relaxed">
          A register of those encountered during the descent — named, classified, and observed.
          Some answer to summons. Most do not.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center pt-4 border-t border-[#333]">
          <input
            className="tiv-input sm:max-w-sm"
            placeholder="search names, types, threats..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="entities-search-input"
          />
          <div className="text-[0.65rem] tracking-[0.3em] uppercase text-[#555] sm:ml-auto">
            {filtered.length} catalogued
          </div>
          {isAdmin && !creating && (
            <button className="tiv-btn" onClick={() => setCreating(true)} data-testid="entities-new-btn">
              + Inscribe New
            </button>
          )}
        </div>

        {isAdmin && creating && (
          <div className="border border-[#333] p-5 bg-[#0a0a0a] flex flex-col sm:flex-row gap-3 sm:items-end">
            <label className="flex-1">
              <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555] mb-1">Provisional Name</div>
              <input
                autoFocus
                className="tiv-input"
                placeholder="e.g. The Veiled Magistrate"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createEntity()}
                data-testid="entities-new-name-input"
              />
            </label>
            <button className="tiv-btn" onClick={createEntity} data-testid="entities-new-save">
              Inscribe
            </button>
            <button className="tiv-btn" onClick={() => { setCreating(false); setNewName(""); }} data-testid="entities-new-cancel">
              Discard
            </button>
          </div>
        )}
      </header>

      {filtered.length === 0 ? (
        <div className="text-[#555] text-xs tracking-widest uppercase border border-dashed border-[#333] p-16 text-center">
          ::: catalogue empty — no entities matched :::
        </div>
      ) : (
        <div className="border-t border-l border-[#333]">
          {filtered.map((e) => (
            <Link
              key={e.id}
              to={`/entities/${e.id}`}
              className="grid grid-cols-12 gap-2 border-r border-b border-[#333] py-4 px-4 hover:bg-[#E3E1D9] hover:text-[#050505] group"
              data-testid={`entity-row-${e.id}`}
            >
              <div className="col-span-2 sm:col-span-1 text-xs tracking-[0.25em] text-[#555] group-hover:text-[#050505]">
                {/* First 4 hex chars of the id, prefix-agnostic — survives
                    future prefix changes (E- → TE- → EIN- → …). */}
                {(e.id.split("-")[1] || e.id).slice(0, 4)}
              </div>
              <div className="col-span-7 sm:col-span-5">
                <div className="font-serif-display text-xl leading-tight">{e.name || <span className="italic text-[#555]">[ unnamed ]</span>}</div>
                <div className="text-[0.65rem] tracking-[0.3em] uppercase text-[#A3A3A3] group-hover:text-[#050505]/70">
                  {e.entity_type || "—"}
                </div>
              </div>
              <div className="hidden sm:block col-span-3 text-xs text-[#A3A3A3] group-hover:text-[#050505]/80 truncate">
                {e.title || "—"}
              </div>
              <div className="col-span-3 sm:col-span-2 text-right">
                <span className="status-pill">{e.threat_level || "—"}</span>
              </div>
              <div className="hidden sm:block col-span-1 text-right text-[0.6rem] tracking-[0.3em] uppercase">
                {e.status}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
